/**
 * Layout for name tags and desk name plates from a class list.
 *
 * Badge tags tile the page in a centred grid; desk plates are tall strips folded in the
 * middle, with the top panel turned upside down so both faces read upright once folded
 * into a tent. A tag's face is laid out in its own box so the same function serves both.
 */

import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { r2 } from "@/lib/tool-kit/printables/page";
import {
  type FontSpec,
  fitSize,
  type TextMeasure,
  truncateToFit,
} from "@/lib/tool-kit/printables/text";

export type TagKind = "badge" | "plate";
export type TagSize = "small" | "medium" | "large";
export type TagStyle = "hello" | "classic" | "plain";
export type TagFont = "bold" | "serif" | "script";

export const MAX_TAGS = 200;

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Badge sizes in millimetres. */
export const BADGE_SIZES: Record<TagSize, { width: number; height: number }> = {
  small: { width: 60, height: 40 },
  medium: { width: 85, height: 55 },
  large: { width: 90, height: 70 },
};

/** Height of one panel of a desk plate; the plate is two panels tall. */
export const PLATE_PANEL: Record<TagSize, number> = {
  small: 30,
  medium: 40,
  large: 55,
};

export interface TagEntry {
  name: string;
  detail: string;
}

const BAR = /\s*\|\s*/;

/** Names, one per line; `Ava | Table 3` adds a second line. */
export function parseTagNames(raw: string): TagEntry[] {
  const entries: TagEntry[] = [];
  for (const line of raw.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const [name = "", ...rest] = trimmed.split(BAR);
    if (name.trim().length > 0) {
      entries.push({ name: name.trim(), detail: rest.join(" ").trim() });
    }
  }
  return entries.slice(0, MAX_TAGS);
}

export interface Grid {
  cells: Box[];
  cols: number;
  rows: number;
}

/** A centred grid of badges that fits the content box; at least one cell. */
export function badgeGrid(geometry: PageGeometry, size: TagSize): Grid {
  const { width, height } = BADGE_SIZES[size];
  const cols = Math.max(1, Math.floor((geometry.contentWidth + 0.01) / width));
  const rows = Math.max(
    1,
    Math.floor((geometry.contentHeight + 0.01) / height)
  );
  const left = geometry.left + (geometry.contentWidth - cols * width) / 2;
  const top = geometry.top + (geometry.contentHeight - rows * height) / 2;
  const cells: Box[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      cells.push({
        x: r2(left + col * width),
        y: r2(top + row * height),
        width,
        height,
      });
    }
  }
  return { cells, cols, rows };
}

export interface PlateGrid {
  /** Whole plates, each two panels tall. */
  plates: Box[];
  panelHeight: number;
}

/** Desk plates stacked down the page, each as wide as the content box. */
export function plateGrid(geometry: PageGeometry, size: TagSize): PlateGrid {
  const panelHeight = PLATE_PANEL[size];
  const plateHeight = panelHeight * 2;
  const count = Math.max(
    1,
    Math.floor((geometry.contentHeight + 0.01) / plateHeight)
  );
  const top = geometry.top + (geometry.contentHeight - count * plateHeight) / 2;
  const plates: Box[] = [];
  for (let i = 0; i < count; i += 1) {
    plates.push({
      x: r2(geometry.left),
      y: r2(top + i * plateHeight),
      width: r2(geometry.contentWidth),
      height: plateHeight,
    });
  }
  return { plates, panelHeight };
}

export interface FaceText {
  text: string;
  x: number;
  y: number;
  size: number;
}

export interface FaceLayout {
  frame: Box | null;
  band: Box | null;
  header: FaceText | null;
  name: FaceText | null;
  /** Write-in line, for a blank tag. */
  rule: { x1: number; x2: number; y: number } | null;
  detail: FaceText | null;
}

export interface FaceOptions {
  style: TagStyle;
  font: TagFont;
  header: string;
  name: string;
  detail: string;
}

export function nameFont(font: TagFont): FontSpec {
  if (font === "script") {
    return { kind: "script" };
  }
  return font === "serif"
    ? { kind: "serif", bold: true }
    : { kind: "sans", bold: true };
}

const DETAIL_FONT: FontSpec = { kind: "sans" };
const HEADER_FONT: FontSpec = { kind: "sans", bold: true };

/** One face (a badge, or one panel of a plate) laid out in `box`, relative to the sheet. */
export function layoutFace(
  box: Box,
  measure: TextMeasure,
  options: FaceOptions
): FaceLayout {
  const pad = Math.max(3, box.width * 0.05);
  const cx = box.x + box.width / 2;
  const width = box.width - pad * 2;
  const hello = options.style === "hello" && options.header.trim().length > 0;
  const bandHeight = hello ? box.height * 0.3 : 0;
  const band: Box | null = hello
    ? { x: box.x, y: box.y, width: box.width, height: bandHeight }
    : null;
  const headerSize = hello
    ? fitSize(
        measure,
        options.header.trim(),
        HEADER_FONT,
        width,
        bandHeight * 0.42,
        2
      )
    : 0;
  const header: FaceText | null = hello
    ? {
        text: options.header.trim(),
        x: r2(cx),
        y: r2(box.y + bandHeight / 2 + headerSize * 0.35),
        size: r2(headerSize),
      }
    : null;

  const hasDetail = options.detail.trim().length > 0;
  const areaTop =
    box.y + bandHeight + (options.style === "classic" ? pad * 0.5 : 0);
  const areaBottom = box.y + box.height - pad * 0.6;
  const detailSize = Math.min(box.height * 0.1, 5.5);
  const detailBlock = hasDetail ? detailSize * 1.9 : 0;
  const nameAreaHeight = areaBottom - areaTop - detailBlock;
  const font = nameFont(options.font);
  const name = options.name.trim();
  const maxName = Math.min(nameAreaHeight * 0.62, box.height * 0.32);
  const size = name ? fitSize(measure, name, font, width, maxName, 3) : 0;
  const baseline = areaTop + nameAreaHeight * 0.5 + (name ? size * 0.33 : 0);

  const detailText = hasDetail
    ? truncateToFit(
        measure,
        options.detail.trim(),
        DETAIL_FONT,
        detailSize,
        width
      )
    : "";
  return {
    frame:
      options.style === "classic"
        ? {
            x: box.x + 1.5,
            y: box.y + 1.5,
            width: box.width - 3,
            height: box.height - 3,
          }
        : null,
    band,
    header,
    name: name
      ? { text: name, x: r2(cx), y: r2(baseline), size: r2(size) }
      : null,
    rule: name
      ? null
      : {
          x1: r2(box.x + pad * 1.4),
          x2: r2(box.x + box.width - pad * 1.4),
          y: r2(areaTop + nameAreaHeight * 0.7),
        },
    detail: hasDetail
      ? {
          text: detailText,
          x: r2(cx),
          y: r2(areaBottom - detailSize * 0.3),
          size: r2(detailSize),
        }
      : null,
  };
}

/** How many sheets `count` tags need at `perPage` per sheet (a blank sheet if none). */
export function sheetCount(count: number, perPage: number): number {
  return Math.max(1, Math.ceil(count / Math.max(1, perPage)));
}
